class CustomPopup {
    private STORAGE_KEY: string;
    private OPEN_DELAY: number;
    static readonly EXPIRY_DURATION = 7 * 24 * 60 * 60 * 1000;

    private readonly wrapper: HTMLElement;
    private readonly popup: HTMLElement;
    private timeout: ReturnType<typeof setTimeout> | undefined;

    constructor(wrapperSelector = '.custom-popup-wrapper') {
        const wrapper = document.querySelector<HTMLElement>(wrapperSelector);
        const popup = wrapper?.querySelector<HTMLElement>('.custom-popup');

        if (!wrapper || !popup) {
            return;
        }

        const position = wrapper.getAttribute('data-position') || 'bottom';
        this.STORAGE_KEY = `custom_popup_${position}`;
        this.OPEN_DELAY = position === 'bottom' ? 5000 : 2000;

        this.wrapper = wrapper;
        this.popup = popup;
        this.timeout = undefined;

        this.scheduleAutoOpen();
        this.bindToggleButtons();
    }

    private scheduleAutoOpen(): void {
        if (!this.hasPopupFlag()) {
            this.timeout = setTimeout(() => {
                this.popup.classList.add('open');
            }, this.OPEN_DELAY);
        }
    }

    private bindToggleButtons(): void {
        this.wrapper.querySelectorAll<HTMLElement>('.custom-popup--toggle').forEach(button => button.addEventListener('click', () => this.toggle()));
    }

    toggle(): void {
        this.popup.classList.toggle('open');

        if (this.popup.classList.contains('open')) {
            this.removePopupFlag();
        } else {
            this.setPopupFlag();
        }

        if (this.timeout !== undefined) {
            clearTimeout(this.timeout);
            this.timeout = undefined;
        }
    }

    private getStoredFlag(): { value: boolean; expires: number } | null {
        try {
            const raw = sessionStorage.getItem(this.STORAGE_KEY);
            if (!raw) return null;

            const data = JSON.parse(raw);

            if (
                data &&
                typeof data === 'object' &&
                'value' in data &&
                'expires' in data
            ) {
                return data as { value: boolean; expires: number };
            }

            return null;
        } catch {
            return null;
        }
    }

    private hasPopupFlag(): boolean {
        const data = this.getStoredFlag();
        if (!data) return false;

        if (Date.now() > data.expires) {
            this.removePopupFlag();
            return false;
        }

        return data.value === true;
    }

    private removePopupFlag(): void {
        try {
            sessionStorage.removeItem(this.STORAGE_KEY);
        } catch {
            // ignore storage errors
        }
    }

    private setPopupFlag(): void {
        try {
            const payload = JSON.stringify({
                value: true,
                expires: Date.now() + CustomPopup.EXPIRY_DURATION,
            });
            sessionStorage.setItem(this.STORAGE_KEY, payload);
        } catch {
            // ignore storage errors
        }
    }
}

if (document.readyState === 'complete') {
    new CustomPopup();
} else {
    window.addEventListener('load', () => new CustomPopup());
}
